import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { careTeamService } from '../../../services/careTeamService';
import api from '../../../config/api';

const QuickBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  // Get provider from URL if coming from provider profile
  const providerId = searchParams.get('provider');
  
  // State management
  const [selectedProvider, setSelectedProvider] = useState(providerId || '');
  const [selectedService, setSelectedService] = useState('consultation');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [consultationMode, setConsultationMode] = useState('in-person');
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [urgency, setUrgency] = useState('regular');
  const [reason, setReason] = useState('');
  const [isNewPatient, setIsNewPatient] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});
  const [providers, setProviders] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showAllProviders, setShowAllProviders] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  // Mock providers data
  const mockProviders = [
    {
      id: '1',
      name: 'Dr. Sarah Johnson',
      title: 'Clinical Psychologist',
      specialties: ['Anxiety', 'Depression'],
      rating: 4.9,
      price: 1500,
      image: '👩‍⚕️',
      availability: 'Available Now',
      nextSlot: '15 mins'
    },
    {
      id: '2',
      name: 'Dr. Rajesh Kumar',
      title: 'Psychiatrist',
      specialties: ['ADHD', 'Bipolar'],
      rating: 4.8,
      price: 2000,
      image: '👨‍⚕️',
      availability: 'Available Today',
      nextSlot: '2:00 PM'
    },
    {
      id: '3',
      name: 'Dr. Priya Nair',
      title: 'Counseling Psychologist',
      specialties: ['Stress', 'Relationships'],
      rating: 4.7,
      price: 1200,
      image: '👩‍⚕️',
      availability: 'Available Today',
      nextSlot: '4:00 PM'
    },
    {
      id: '4',
      name: 'Dr. Ahmed Hassan',
      title: 'Clinical Psychologist',
      specialties: ['OCD', 'Phobias'],
      rating: 4.9,
      price: 1800,
      image: '👨‍⚕️',
      availability: 'Available Tomorrow',
      nextSlot: '10:00 AM'
    },
    {
      id: '5',
      name: 'Dr. Lakshmi Menon',
      title: 'Child Psychologist',
      specialties: ['Child Development', 'Autism'],
      rating: 4.8,
      price: 1600,
      image: '👩‍⚕️',
      availability: 'Available Today',
      nextSlot: '5:30 PM'
    }
  ];

  // Quick service options
  const quickServices = [
    { value: 'consultation', label: 'Quick Consultation', icon: '💬', duration: '30 min' },
    { value: 'urgent', label: 'Urgent Support', icon: '🆘', duration: '15-30 min' },
    { value: 'follow-up', label: 'Follow-up', icon: '📋', duration: '20 min' },
    { value: 'prescription', label: 'Prescription Refill', icon: '💊', duration: '15 min' }
  ];

  // Common reasons for quick booking
  const commonReasons = [
    'Anxiety/Panic Attack',
    'Feeling Depressed',
    'Stress Management',
    'Sleep Issues',
    'Medication Question',
    'Crisis Support',
    'Relationship Issues',
    'Work Stress',
    'Other'
  ];

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, []);

  // Load providers from API
  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      const response = await careTeamService.getPublicCareTeamList({ per_page: 100 });
      if (response.success) {
        setProviders(response.data.items || []);
      } else {
        console.error('Failed to load providers:', response.error);
        // Fallback to mock data if API fails
        setProviders(mockProviders);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
      // Fallback to mock data if API fails
      setProviders(mockProviders);
    } finally {
      setLoadingProviders(false);
    }
  };

  // Load free slots for selected provider and date
  const loadFreeSlots = async (userId, date) => {
    try {
      setLoadingSlots(true);
      
      const dateObj = new Date(date);
      // Convert to Monday=0, Sunday=6 format
      const weekDay = (dateObj.getDay() + 6) % 7; // 0=Monday, 1=Tuesday, ..., 6=Sunday
      
      const response = await api.get(`/planify/event/public/user/${userId}/free-slot`, {
        params: {
          date: date,
          week_day: weekDay
        }
      });

      if (response.data && Array.isArray(response.data)) {
        // Format the slots to show start_time and end_time
        const formattedSlots = response.data.map(slot => {
          const startTime = new Date(slot.start_time);
          const endTime = new Date(slot.end_time);
          
          // Format time to 12-hour format
          const formatTime = (date) => {
            return date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
          };
          
          return {
            time: formatTime(startTime),
            start_time: slot.start_time,
            end_time: slot.end_time,
            availability_id: slot.availability_id,
            available: true,
            display_time: `${formatTime(startTime)} - ${formatTime(endTime)}`,
            start_display: formatTime(startTime),
            end_display: formatTime(endTime)
          };
        });
        
        setAvailableSlots(formattedSlots);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error loading free slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Get next 7 days for quick date selection
  const getNext7Days = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      dates.push({
        value: date.toISOString().split('T')[0],
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short' }),
        day: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' })
      });
    }
    
    return dates;
  };

  // Handle date selection
  const handleDateSelection = (date) => {
    setSelectedDate(date);
    setSelectedTime(''); // Reset selected time when date changes
    setSelectedSlot(null); // Reset selected slot when date changes
    
    // Load free slots for selected provider and date
    if (selectedProvider) {
      loadFreeSlots(selectedProvider, date);
    }
  };

  // Handle provider selection
  const handleProviderSelection = (providerId) => {
    setSelectedProvider(providerId);
    setSelectedTime(''); // Reset selected time when provider changes
    setSelectedSlot(null); // Reset selected slot when provider changes
    
    // Load free slots if date is already selected
    if (selectedDate) {
      loadFreeSlots(providerId, selectedDate);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedProvider) newErrors.provider = 'Please select a provider';
    if (!selectedDate) newErrors.date = 'Please select a date';
    if (!selectedTime) newErrors.time = 'Please select a time';
    if (!patientName.trim()) newErrors.name = 'Name is required';
    /*if (!patientEmail.trim()) {
      newErrors.email = 'Email is required';
    } */if (patientEmail && !/\S+@\S+\.\S+/.test(patientEmail)) {
      newErrors.email = 'Invalid email format';
    }
    if (!patientPhone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{9,14}$/.test(patientPhone.replace(/\s/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!reason) newErrors.reason = 'Please select or describe your reason';
    if (!agreedToTerms) newErrors.terms = 'You must agree to the terms';
    
    // Validate customer ID for returning patients
    if (!isNewPatient && !customerId.trim()) {
      newErrors.customerId = 'Patient ID is required for returning patients';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format time for API (extract time from ISO datetime in local timezone)
  const formatTimeForAPI = (isoDateTime) => {
    const date = new Date(isoDateTime);
    // Use local time instead of UTC since we're sending timezone as "Asia/Kolkata"
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
    
    return `${hours}:${minutes}:${seconds}.${milliseconds}Z`;
  };

  // Handle quick booking submission
  const handleQuickBook = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.text-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setLoading(true);
    
    try {
      const selectedProviderData = providers.find(p => p.user_id === selectedProvider);
      
      if (!selectedProviderData) {
        throw new Error('Selected provider not found');
      }

      // Check if slot is selected
      if (!selectedSlot) {
        throw new Error('Please select a time slot');
      }

      // Prepare booking payload
      const bookingPayload = {
        booking_data: {
          availability_id: selectedSlot.availability_id,
          booked_by_email: patientEmail || "",
          booked_by_name: patientName,
          custom_fields: {
            preferred_language: "English",
            status: "waiting"
          },
          end_date: selectedDate,
          end_time: formatTimeForAPI(selectedSlot.end_time),
          event_type_id: selectedProviderData.event_type_id,
          mobile: patientPhone,
          note: reason,
          start_date: selectedDate,
          start_time: formatTimeForAPI(selectedSlot.start_time),
          timezone: "Asia/Kolkata"
        }
      };

      // Add customer_id only for returning patients
      if (!isNewPatient && customerId.trim()) {
        bookingPayload.booking_data.customer_id = customerId.trim();
      }

      // Call booking API
      const response = await api.post('/planify/booking/public/booking', bookingPayload);
      
      if (response.data && response.data.success === true) {
        // Success
        setModalType('success');
        setModalTitle('Booking Confirmed!');
        setModalMessage('Your appointment has been successfully booked. You will receive a confirmation email shortly.');
        setShowModal(true);
        
        // Clear all form fields on success
        clearForm();
      } else {
        // API returned success: false
        setModalType('error');
        setModalTitle('Booking Failed');
        setModalMessage(response.data?.message || 'Failed to book appointment. Please try again.');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Booking error:', error);
      setModalType('error');
      setModalTitle('Booking Failed');
      setModalMessage(error.response?.data?.message || error.message || 'Failed to book appointment. Please try again.');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Clear all form fields
  const clearForm = () => {
    setSelectedProvider('');
    setSelectedService('consultation');
    setSelectedDate('');
    setSelectedTime('');
    setSelectedSlot(null);
    setConsultationMode('video');
    setPatientName('');
    setPatientEmail('');
    setPatientPhone('');
    setUrgency('regular');
    setReason('');
    setIsNewPatient(true);
    setCustomerId('');
    setAgreedToTerms(false);
    setErrors({});
  };

  // Handle immediate booking (skip to available slot)
  const handleBookNow = (provider, slot) => {
    setSelectedProvider(provider.user_id);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedTime(slot.time);
    setSelectedSlot(slot);
    
    // Scroll to contact info section
    const contactSection = document.getElementById('contact-info');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Success/Error Modal
  const BookingModal = () => (
    <AnimatePresence>
      {showModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl max-w-md w-full p-8 text-center"
          >
            <div className={`text-6xl mb-4 ${modalType === 'success' ? 'text-green-500' : 'text-red-500'}`}>
              {modalType === 'success' ? '✅' : '❌'}
            </div>
            <h2 className={`text-2xl font-bold mb-2 ${modalType === 'success' ? 'text-gray-900' : 'text-red-600'}`}>
              {modalTitle}
            </h2>
            <p className="text-gray-600 mb-6">
              {modalMessage}
            </p>
            <div className="flex gap-3 justify-center">
              {modalType === 'success' ? (
                <>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      clearForm();
                    }}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Book Another
                  </button>
                  <button
                    onClick={() => navigate('/booking/find-provider')}
                    className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                  >
                    Close
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                >
                  Try Again
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Quick Booking</h1>
              <p className="text-sm text-gray-600">Book your appointment in under 2 minutes</p>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center text-sm text-gray-600">
                <span className="text-green-500 mr-1">●</span>
                <span>Available Now</span>
              </div>
              <Link
                to="/booking/book-appointment"
                className="text-blue-500 hover:text-blue-600 font-medium"
              >
                Full Booking →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Banner */}
      {urgency === 'crisis' && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 md:mx-auto md:max-w-6xl mt-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">🆘</span>
            <div className="flex-1">
              <p className="font-semibold text-red-800">Crisis Support Available</p>
              <p className="text-sm text-red-700">
                If you're in immediate danger, please call emergency services (112) or our 24/7 crisis helpline.
              </p>
            </div>
            <a
              href="tel:+18884047470"
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
            >
              Call Now
            </a>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Quick Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Urgency Level */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <span className="text-2xl mr-2">⚡</span>
                How urgent is your need?
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={() => setUrgency('regular')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    urgency === 'regular'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">😊</span>
                  <p className="font-medium mt-2">Regular</p>
                  <p className="text-xs text-gray-600">Within 24-48 hours</p>
                </button>
                <button
                  onClick={() => setUrgency('urgent')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    urgency === 'urgent'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">⚠️</span>
                  <p className="font-medium mt-2">Urgent</p>
                  <p className="text-xs text-gray-600">Within 2-4 hours</p>
                </button>
                <button
                  onClick={() => setUrgency('crisis')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    urgency === 'crisis'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">🆘</span>
                  <p className="font-medium mt-2">Crisis</p>
                  <p className="text-xs text-gray-600">Immediate help needed</p>
                </button>
              </div>
            </motion.div>

            {/* Quick Provider Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center justify-between">
                <span>
                  <span className="text-2xl mr-2">👨‍⚕️</span>
                  Available Providers
                </span>
                <button
                  onClick={() => setShowAllProviders(!showAllProviders)}
                  className="text-sm text-blue-500 hover:text-blue-600"
                >
                  {showAllProviders ? 'Show Less' : 'Show All'}
                </button>
              </h2>

              <div className="space-y-3">
                {providers.slice(0, showAllProviders ? providers.length : 3).map((provider) => {
                  const nextSlot = availableSlots.find(s => s.available);
                  return (
                    <div
                      key={provider.user_id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedProvider === provider.user_id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleProviderSelection(provider.user_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-3xl">👩‍⚕️</span>
                          <div>
                            <p className="font-medium">{provider.user_name}</p>
                            <p className="text-sm text-gray-600">{provider.professional_title}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-gray-500">
                                ⭐ {provider.rating}
                              </span>
                              <span className="text-xs text-gray-500">
                                ₹{provider.consultation_fee}
                              </span>
                              <span className="text-xs text-green-600 font-medium">
                                {provider.availability_status}
                              </span>
                            </div>
                          </div>
                        </div>
                        {provider.availability === 'Available Now' && nextSlot && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookNow(provider, nextSlot);
                            }}
                            className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors"
                          >
                            Book {nextSlot.time}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {!showAllProviders && providers.length > 3 && (
                <div className="mt-4 text-center">
                  <Link
                    to="/booking/find-provider"
                    className="text-blue-500 hover:text-blue-600 font-medium"
                  >
                    Browse All Providers →
                  </Link>
                </div>
              )}

              {errors.provider && (
                <p className="text-red-500 text-sm mt-2">{errors.provider}</p>
              )}
            </motion.div>

            {/* Quick Date & Time Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h2 className="text-lg font-semibold mb-4">
                <span className="text-2xl mr-2">📅</span>
                Select Date & Time
              </h2>

              {/* Date Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose Date
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {getNext7Days().map((date) => (
                    <button
                      key={date.value}
                      onClick={() => handleDateSelection(date.value)}
                      className={`p-2 text-center rounded-lg border transition-all ${
                        selectedDate === date.value
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      <p className="text-xs font-medium">{date.label}</p>
                      {date.label !== 'Today' && date.label !== 'Tomorrow' && (
                        <p className="text-lg font-bold">{date.day}</p>
                      )}
                    </button>
                  ))}
                </div>
                {errors.date && (
                  <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                )}
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Slots
                  </label>
                  {loadingSlots ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="text-gray-600 mt-2 text-sm">Loading available slots...</p>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedTime(slot.time);
                            setSelectedSlot(slot);
                          }}
                          className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                            selectedTime === slot.time
                              ? 'bg-blue-500 text-white border-blue-500'
                              : 'bg-white hover:bg-gray-50 border-gray-300'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-600">
                      <p className="text-sm">No available slots for this date.</p>
                      <p className="text-xs mt-1">Please select a different date.</p>
                    </div>
                  )}
                  {errors.time && (
                    <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                  )}
                </div>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              id="contact-info"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h2 className="text-lg font-semibold mb-4">
                <span className="text-2xl mr-2">📝</span>
                Your Information
              </h2>

              {/* New/Returning Patient */}
              <div className="mb-4">
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={isNewPatient}
                      onChange={() => setIsNewPatient(true)}
                      className="mr-2"
                    />
                    <span>New Patient</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!isNewPatient}
                      onChange={() => setIsNewPatient(false)}
                      className="mr-2"
                    />
                    <span>Returning Patient</span>
                  </label>
                </div>
                
                {/* Patient ID field for returning patients */}
                {!isNewPatient && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patient ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your Patient ID"
                    />
                    {errors.customerId && (
                      <p className="text-red-500 text-sm mt-1">{errors.customerId}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={patientPhone}
                    onChange={(e) => setPatientPhone(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+1 98765 43210"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={patientEmail}
                    onChange={(e) => setPatientEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Reason for Visit */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Visit <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {commonReasons.map((reasonOption) => (
                    <button
                      key={reasonOption}
                      onClick={() => setReason(reasonOption)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                        reason === reasonOption
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {reasonOption}
                    </button>
                  ))}
                </div>
                {reason === 'Other' && (
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Please describe your reason..."
                  />
                )}
                {errors.reason && (
                  <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
                )}
              </div>
            </motion.div>
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Booking Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-xl shadow-md p-6"
              >
                <h3 className="text-lg font-semibold mb-4">Booking Summary</h3>
                
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-gray-600">Provider</p>
                    <p className="font-medium">
                      {selectedProvider
                        ? providers.find(p => p.user_id === selectedProvider)?.user_name
                        : 'Not selected'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-gray-600">Date & Time</p>
                    <p className="font-medium">
                      {selectedDate && selectedTime
                        ? `${new Date(selectedDate).toLocaleDateString()} at ${selectedTime}`
                        : 'Not selected'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-gray-600">Consultation Mode</p>
                    <div className="flex items-center gap-2 mt-1">
                      {/* <button
                        onClick={() => setConsultationMode('video')}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          consultationMode === 'video'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        📹 Video
                      </button> */}
                      <button
                        onClick={() => setConsultationMode('in-person')}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          consultationMode === 'in-person'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        🏥 In-Person
                      </button>
                      {/* <button
                        onClick={() => setConsultationMode('phone')}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          consultationMode === 'phone'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        📞 Phone
                      </button> */}
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t">
                    <p className="text-gray-600">Estimated Fee</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ₹{selectedProvider
                        ? providers.find(p => p.user_id === selectedProvider)?.consultation_fee
                        : '---'}
                    </p>
                  </div>
                </div>

                {/* Terms Agreement */}
                <div className="mt-4 pt-4 border-t">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 mr-2"
                    />
                    <span className="text-xs text-gray-600">
                      I agree to the{' '}
                      <Link to="/terms" className="text-blue-500 hover:text-blue-600">
                        Terms
                      </Link>
                      {' '}and understand the cancellation policy
                    </span>
                  </label>
                  {errors.terms && (
                    <p className="text-red-500 text-xs mt-1">{errors.terms}</p>
                  )}
                </div>

                {/* Book Button */}
                <button
                  onClick={handleQuickBook}
                  disabled={loading}
                  className="w-full mt-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Booking...
                    </span>
                  ) : (
                    '⚡ Quick Book Now'
                  )}
                </button>

                {/* Alternative Actions */}
                <div className="mt-4 text-center">
                  <Link
                    to="/booking/book-appointment"
                    className="text-sm text-gray-600 hover:text-blue-600"
                  >
                    Need more options? Use full booking →
                  </Link>
                </div>
              </motion.div>

              {/* Help Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6"
              >
                <h4 className="font-semibold text-gray-900 mb-2">
                  Need Assistance?
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Our support team is here to help you book your appointment.
                </p>
                <a
                  href="tel:+18884047470"
                  className="flex items-center justify-center w-full py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  <span className="mr-2">📞</span>
                  Call +1 (888) 404-7470
                </a>
              </motion.div>

              {/* Trust Badges */}
              <div className="bg-white rounded-xl p-4 text-center">
                <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                  <div>
                    <span className="text-green-500 text-lg">🔒</span>
                    <p>Secure & Confidential</p>
                  </div>
                  <div>
                    <span className="text-blue-500 text-lg">✓</span>
                    <p>Verified Providers</p>
                  </div>
                  <div>
                    <span className="text-yellow-500 text-lg">⭐</span>
                    <p>4.8+ Rating</p>
                  </div>
                  <div>
                    <span className="text-purple-500 text-lg">💬</span>
                    <p>24/7 Support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Quick Actions */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4">
          <button
            onClick={handleQuickBook}
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {loading ? 'Booking...' : '⚡ Quick Book Now'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <BookingModal />
    </div>
  );
};

export default QuickBooking;